import { Component, DestroyRef, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { take } from "rxjs";
import type { AbstractControl } from "@angular/forms";
import {
  FormArray,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { Store } from "@ngrx/store";
import { MatDialog } from "@angular/material/dialog";
import { MatTableDataSource } from "@angular/material/table";
import {
  EmployeeSetting,
  PredefinedEmployeeSetting,
} from "../settings/settings.interface";
import {
  ChangeEmployeeSettingsAction,
  CHANGE_EMPLOYEE_SETTINGS_FAILURE,
  CHANGE_EMPLOYEE_SETTINGS_SUCCESS,
  GetEmployeeSettingsAction,
  GET_EMPLOYEE_SETTINGS_FAILURE,
  GET_EMPLOYEE_SETTINGS_SUCCESS,
} from "../settings/settings.actions";
import {
  getEmployeeSettingsSelector,
  getPredefinedEmployeeSettingsSelector,
} from "../settings/settings.reducer";
import { EmployeeSettingsFormDialogComponent } from "./employee-settings-form-dialog/employee-settings-form-dialog.component";
import type {
  EmployeeSettingsFormDialogData,
  EmployeeSettingsFormDialogResult,
} from "./employee-settings.interface";
import { ConfirmationDialogComponent } from "../shared/confirmation-dialog/confirmation-dialog.component";
import { TranslateService } from "@ngx-translate/core";
import { scrollToBottom } from "../shared/utils/window.utils";
import { mediaFilePreview } from "../shared/utils/media-file-preview.util";
import { Router } from "@angular/router";
import { AuthService } from "../auth/auth.service";
import { StartCheckingForInstallationInProgressAction } from "../signature-installation-state/signature-installation-state.actions";
import { ShowOnSubmitAttemptErrorStateMatcher } from "../shared/error-state-matcher";
import { Actions, ofType } from "@ngrx/effects";
import { ImagesService } from "../media/images.service";
import type { Image } from "../media/media.interface";
import type { HardcodedVariableIcon } from "../digital-cards/digital-cards.interface";
import {
  HARDCODED_VARIABLE_ICONS,
  hardcodedIconByValue,
  hardcodedIconForVariable,
} from "../digital-cards/variable-icons";

@Component({
  selector: "app-employee-settings",
  templateUrl: "./employee-settings.component.html",
  styleUrls: ["./employee-settings.component.scss"],
  standalone: false,
})
export class EmployeeSettingsComponent implements OnInit {
  form: UntypedFormGroup = new UntypedFormGroup({
    employeeSettings: new FormArray([]),
  });

  saving = false;
  hasChanges = false;
  errorStateMatcher = new ShowOnSubmitAttemptErrorStateMatcher();
  displayedColumns: string[] = [
    "customField",
    "customLabel",
    "defaultValue",
    "icon",
    "mandatory",
    "overridable",
    "actions",
  ];
  dataSource = new MatTableDataSource<UntypedFormGroup>();
  predefinedEmployeeSettings: PredefinedEmployeeSetting[] = [];
  hardcodedIcons: HardcodedVariableIcon[] = HARDCODED_VARIABLE_ICONS;
  readonly mediaFilePreview = mediaFilePreview;
  mediaImages: Image[] = [];
  mediaImagesLoading = false;
  private initialFormValue: string | null = null;
  private initialDefaultValues: Map<string, string> = new Map();
  private initialVariableNames: Set<string> = new Set();
  private employeeSettingsLoaded = false;

  constructor(
    private store: Store,
    private actions$: Actions,
    private dialog: MatDialog,
    private translate: TranslateService,
    private router: Router,
    private auth: AuthService,
    private destroyRef: DestroyRef,
    private imagesService: ImagesService,
  ) {}

  ngOnInit(): void {
    this.mediaImagesLoading = true;
    this.store.dispatch(new GetEmployeeSettingsAction());
    this.imagesService
      .getImages()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (list) => {
          this.mediaImages = Array.isArray(list) ? list : [];
          this.mediaImagesLoading = false;
        },
        error: () => {
          this.mediaImages = [];
          this.mediaImagesLoading = false;
        },
      });

    this.actions$
      .pipe(
        ofType(GET_EMPLOYEE_SETTINGS_SUCCESS, GET_EMPLOYEE_SETTINGS_FAILURE),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.employeeSettingsLoaded = true;
      });

    this.store
      .select(getEmployeeSettingsSelector)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((employeeSettings) => {
        (this.form.get("employeeSettings") as FormArray).clear();
        this.initialVariableNames.clear();
        this.initialDefaultValues.clear();

        const ordered = [
          ...employeeSettings.filter((setting) => setting.entityBound),
          ...employeeSettings.filter((setting) => !setting.entityBound),
        ];

        ordered.forEach((setting) => {
          this.addEmployeeSetting(setting);
          this.initialDefaultValues.set(
            setting.name,
            setting.defaultValueLocked ? "" : setting.defaultValue || "",
          );
          this.initialVariableNames.add(setting.name);
        });

        this.initialFormValue = JSON.stringify(this.form.getRawValue());
        this.hasChanges = false;
        this.updateDataSource();
      });

    this.store
      .select(getPredefinedEmployeeSettingsSelector)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((predefinedEmployeeSettings) => {
        this.predefinedEmployeeSettings = predefinedEmployeeSettings;
      });

    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.checkForChanges();
      });
  }

  get employeeSettingsList() {
    return this.form.get("employeeSettings") as FormArray;
  }

  get isLoading(): boolean {
    return !this.employeeSettingsLoaded || this.mediaImagesLoading;
  }

  private updateDataSource() {
    this.dataSource.data = this.employeeSettingsList
      .controls as unknown as UntypedFormGroup[];
  }

  addEmployeeSetting = (setting: EmployeeSetting) => {
    this.employeeSettingsList.push(
      new UntypedFormGroup({
        name: new UntypedFormControl(setting.name, (c: AbstractControl) =>
          Validators.required(c),
        ),
        defaultValue: new UntypedFormControl({
          value: setting.defaultValueLocked ? "" : setting.defaultValue || "",
          disabled: Boolean(setting.defaultValueLocked),
        }),
        overridable: new UntypedFormControl({
          value: Boolean(setting.overridable),
          disabled: Boolean(setting.overridableLocked),
        }),
        mandatory: new UntypedFormControl({
          value: Boolean(setting.mandatory),
          disabled: Boolean(setting.mandatoryLocked),
        }),
        label: new UntypedFormControl(setting.label, (c: AbstractControl) =>
          Validators.required(c),
        ),
        iconUrl: new UntypedFormControl(
          setting.iconUrl?.trim() ||
            hardcodedIconForVariable(setting.name).value,
          (c: AbstractControl) => Validators.required(c),
        ),
        entityBound: new UntypedFormControl(Boolean(setting.entityBound)),
        mandatoryLocked: new UntypedFormControl(
          Boolean(setting.mandatoryLocked),
        ),
        overridableLocked: new UntypedFormControl(
          Boolean(setting.overridableLocked),
        ),
        defaultValueLocked: new UntypedFormControl(
          Boolean(setting.defaultValueLocked),
        ),
        removable: new UntypedFormControl(setting.removable !== false),
      }),
    );
  };

  canRemoveSetting(row: AbstractControl | null | undefined): boolean {
    return row?.get("removable")?.value !== false;
  }

  removeEmployeeSetting(index: number) {
    const row = this.employeeSettingsList.at(index);
    if (!this.canRemoveSetting(row)) {
      return;
    }
    this.employeeSettingsList.removeAt(index);
  }

  confirmRemoveEmployeeSetting(index: number): void {
    const row = this.employeeSettingsList.at(index);
    const fieldName = String(row?.get("name")?.value ?? "");
    if (!this.canRemoveSetting(row)) {
      return;
    }

    const dialogData = {
      title: this.translate.instant("settings.removeFieldTitle") as string,
      body: this.translate.instant("settings.removeFieldMessage", {
        field: fieldName,
      }) as string,
      confirmButton: this.translate.instant("delete") as string,
    };

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.removeEmployeeSetting(index);
        this.updateDataSource();
      }
    });
  }

  onSubmit = () => {
    this.errorStateMatcher.submitAttempted = true;
    if (this.form.valid) {
      this.saving = true;

      const hasDefaultValuesChanged = this.hasDefaultValuesChanged();
      const employeeSettings = (
        this.form.getRawValue() as {
          employeeSettings: Array<
            EmployeeSetting & {
              defaultValueLocked?: boolean;
              overridableLocked?: boolean;
              mandatoryLocked?: boolean;
            }
          >;
        }
      ).employeeSettings.map((setting) => ({
        name: setting.name,
        label: setting.label,
        iconUrl: setting.iconUrl,
        defaultValue: setting.defaultValueLocked
          ? null
          : setting.defaultValue || null,
        overridable: Boolean(setting.overridable),
        mandatory: Boolean(setting.mandatory),
      }));
      const currentVariableNames = new Set(employeeSettings.map((s) => s.name));
      const hasRemovedVariables = Array.from(this.initialVariableNames).some(
        (name) => !currentVariableNames.has(name),
      );

      if (hasRemovedVariables) {
        this.store.dispatch(new StartCheckingForInstallationInProgressAction());
      }

      this.store.dispatch(
        new ChangeEmployeeSettingsAction(employeeSettings as EmployeeSetting[]),
      );

      this.actions$
        .pipe(
          ofType(
            CHANGE_EMPLOYEE_SETTINGS_SUCCESS,
            CHANGE_EMPLOYEE_SETTINGS_FAILURE,
          ),
          take(1),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe((action: { type: string }) => {
          this.saving = false;
          if (action.type === CHANGE_EMPLOYEE_SETTINGS_FAILURE) {
            return;
          }
          this.hasChanges = false;
          this.initialVariableNames = new Set(
            employeeSettings.map((s) => s.name),
          );
          this.initialDefaultValues = this.currentDefaultValues();
          this.initialFormValue = JSON.stringify(this.form.getRawValue());

          if (hasDefaultValuesChanged) {
            this.showSyncRequiredDialog();
          }
        });
    }
  };

  addNewEmployeeSetting = () => {
    const existingFieldNames = (
      this.employeeSettingsList.getRawValue() as { name: string }[]
    ).map((r) => r.name);

    const dialogRef = this.dialog.open<
      EmployeeSettingsFormDialogComponent,
      EmployeeSettingsFormDialogData,
      EmployeeSettingsFormDialogResult | undefined
    >(EmployeeSettingsFormDialogComponent, {
      width: "500px",
      panelClass: "app-dialog-panel",
      data: {
        existingFieldNames,
        predefinedEmployeeSettings: this.predefinedEmployeeSettings,
        mediaImages: this.mediaImages,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.addEmployeeSetting({
          domain: "",
          name: result.name,
          label: result.label,
          defaultValue: result.defaultValue,
          iconUrl: result.iconUrl,
          overridable: result.overridable,
          mandatory: result.mandatory,
          entityBound: false,
          mandatoryLocked: false,
          overridableLocked: false,
          defaultValueLocked: false,
          removable: true,
        });
        this.updateDataSource();
        this.onSubmit();
        scrollToBottom();
      }
    });
  };

  private hasDefaultValuesChanged(): boolean {
    return this.employeeSettingsList.controls.some((control) => {
      const name = control.get("name")?.value as string;
      const currentValue = (control.get("defaultValue")?.value as string) || "";
      const initialValue = this.initialDefaultValues.get(name) || "";
      return currentValue !== initialValue;
    });
  }

  private currentDefaultValues(): Map<string, string> {
    return new Map(
      this.employeeSettingsList.controls.map((control) => {
        const name = control.get("name")?.value as string;
        const value = (control.get("defaultValue")?.value as string) || "";
        return [name, value];
      }),
    );
  }

  iconSrc(url: string | null | undefined): string {
    if (!url) {
      return "";
    }
    const hardcoded = hardcodedIconByValue(url);
    return hardcoded?.preview ?? url;
  }

  iconLabel(url: string | null | undefined): string {
    if (!url) {
      return "";
    }
    const hardcoded = hardcodedIconByValue(url);
    if (hardcoded) {
      return hardcoded.label;
    }
    const media = this.mediaImages.find((image) => image.imageUrl === url);
    return media?.name?.trim() || url;
  }

  isUnknownIconUrl(url: string | null | undefined): boolean {
    if (!url?.trim()) {
      return false;
    }
    if (hardcodedIconByValue(url)) {
      return false;
    }
    return !this.mediaImages.some((image) => image.imageUrl === url);
  }

  private checkForChanges() {
    const currentFormValue = JSON.stringify(this.form.getRawValue());
    this.hasChanges = currentFormValue !== this.initialFormValue;
  }

  private showSyncRequiredDialog(): void {
    const dialogData = {
      title: this.translate.instant(
        "settings.integration.syncRequired.title",
      ) as string,
      body: this.translate.instant(
        "settings.integration.syncRequired.message",
      ) as string,
      confirmButton: this.translate.instant(
        "settings.integration.syncRequired.syncNow",
      ) as string,
    };

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        const provider = this.auth.appUser?.provider;
        void this.router.navigate(["/settings/integration"], {
          queryParams: { sync: provider?.toLowerCase() || "auto" },
        });
      }
    });
  }
}
